import React from 'react';
import { Modal, Field } from '../../components/ui';
import { OTHER_FUNCTION_TYPE } from '../../utils/helpers';
import FunctionServices from './FunctionServices';

// Fields placed by hand below; anything else the hook adds later still shows
// (in the generic block at the end) so nothing is ever lost.
const PLACED = ['FunctionName', 'FunctionType', 'FunctionTypeOther', 'FunctionDate', 'StartTime', 'EndTime', 'Status',
  'Venue', 'City', 'VenueAddress', 'GpsLocation', 'Description'];

const minutes = (t) => {
  const m = /^(\d{1,2}):(\d{2})/.exec(String(t || ''));
  return m ? Number(m[1]) * 60 + Number(m[2]) : null;
};
const duration = (a, b) => {
  const s = minutes(a); const e = minutes(b);
  if (s === null || e === null || e <= s) return '';
  const d = e - s;
  return `${Math.floor(d / 60) ? `${Math.floor(d / 60)} h` : ''}${d % 60 ? ` ${d % 60} min` : ''}`.trim();
};

// Desktop add/edit Function dialog. Every box only as wide as its content:
//   name | type (| new type name, only for "Other")
//   date | start | end | status   (+ how long it runs)
//   venue | city
//   venue address | GPS
//   description
// then the catering / event-management panel - a function is planned in one
// place. The panel needs a saved function, so it appears only when editing.
// Enter in a one-line box saves.
export default function FunctionFormDesktop({ vm, event }) {
  const { editing, values, fields, saving, setField, save, closeForm } = vm;
  if (!vm.open) return null;

  const f = (key) => fields.find(x => x.key === key);
  const onEnter = (e) => { if (e.key === 'Enter') { e.preventDefault(); save(); } };
  const isOther = values.FunctionType === OTHER_FUNCTION_TYPE;
  const len = duration(values.StartTime, values.EndTime);

  // One field, rendered the way its definition asks. Text / select / date are
  // plain boxes (so they can take autofocus and Enter); time goes through
  // Field → the typed box + clock dial; textareas stay textareas.
  const box = (key, extra = {}) => {
    const def = f(key);
    if (!def) return null;
    const type = def.type || 'text';
    const label = <>{extra.label || def.label}{def.required && ' *'}</>;
    if (type === 'time') {
      return <Field key={key} label={def.label} type="time" value={values[key]} required={def.required} onChange={(v) => setField(def, v)} />;
    }
    if (type === 'textarea') {
      return (
        <label key={key} className="field">{label}
          <textarea rows={def.rows || 2} value={values[key] ?? ''} placeholder={extra.placeholder}
            onChange={(e) => setField(def, e.target.value)} />
        </label>
      );
    }
    if (type === 'select') {
      return (
        <label key={key} className="field">{label}
          <select value={values[key] ?? ''} onChange={(e) => setField(def, e.target.value)}>
            <option value="">- select -</option>
            {(def.options || []).map(o => <option key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</option>)}
          </select>
        </label>
      );
    }
    return (
      <label key={key} className="field">{label}
        <input type={type} value={values[key] ?? ''} autoFocus={extra.autoFocus} placeholder={extra.placeholder}
          inputMode={type === 'number' ? 'decimal' : undefined}
          onChange={(e) => setField(def, e.target.value)} onKeyDown={onEnter} />
      </label>
    );
  };

  const rest = fields.filter(x => !PLACED.includes(x.key));

  return (
    <Modal
      dense className="crud-form-modal ev-fn-modal"
      title={`${editing ? 'Edit' : 'Add'} Function`}
      onClose={closeForm}
      footer={<>
        <span className="ev-summary">
          {values.FunctionName
            ? <><strong>{values.FunctionName}</strong>{len ? ` · ${len}` : ''}</>
            : 'Name and date are needed; the rest can follow'}
        </span>
        <button className="btn light" onClick={closeForm}>Cancel</button>
        <button className="btn primary" disabled={saving} onClick={save}>{saving ? 'Saving…' : 'Save'}</button>
      </>}
    >
      <div className={'ev-row ev-fn-r1' + (isOther ? ' with-other' : '')}>
        {box('FunctionName', { autoFocus: !editing, placeholder: 'e.g. Haldi' })}
        {box('FunctionType')}
        {isOther && box('FunctionTypeOther', { label: 'New type name', placeholder: 'Type the new type' })}
      </div>
      <div className="ev-row ev-fn-r2">
        {box('FunctionDate')}
        {box('StartTime')}
        {box('EndTime')}
        {box('Status')}
      </div>
      <div className="ev-row ev-fn-r3">
        {box('Venue', { placeholder: 'Hall / lawn / home' })}
        {box('City')}
      </div>
      <div className="ev-row ev-fn-r4">
        {box('VenueAddress')}
        {box('GpsLocation', { placeholder: 'Paste a Google Maps link' })}
      </div>
      <div className="ev-row ev-fn-r5">
        {box('Description')}
        {rest.map(x => box(x.key))}
      </div>

      {editing
        ? <FunctionServices event={event} fn={{ ...editing, ...values }} />
        : <div className="fn-services muted" style={{ fontSize: 12.5 }}>
            Save the function first — catering and the other services are added to it afterwards.
          </div>}
    </Modal>
  );
}
